import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useFinalizarManutencao, useDocumentos } from '../hooks';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';

interface Props {
  open: boolean;
  onClose: () => void;
  assetId: string;
}

export function MaintenanceFinishModal({ open, onClose, assetId }: Props) {
  const mutation = useFinalizarManutencao(assetId);
  const documentsQuery = useDocumentos(assetId);
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setDescription(''); setCost(''); setDocumentId(''); setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!description.trim()) { setError('Descreva o retorno da manutenção'); return; }
    const costNumber = cost ? Number(cost.replace(',', '.')) : undefined;
    if (costNumber !== undefined && (Number.isNaN(costNumber) || costNumber < 0)) {
      setError('Custo inválido'); return;
    }
    try {
      await mutation.mutateAsync({
        description: description.trim(),
        cost: costNumber,
        documentId: documentId || undefined,
      });
      reset(); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao finalizar manutenção'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="text-lg font-semibold text-gray-900">Registrar retorno de manutenção</Dialog.Title>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <Textarea
              label="Descrição do serviço executado *"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
            <TextInput
              label="Custo (opcional) — ex.: 350,00"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              inputMode="decimal"
            />
            <div>
              <AutocompleteSelect
                label="Ordem de manutenção (documento, opcional)"
                options={[
                  { value: '', label: 'Nenhum documento' },
                  ...(documentsQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.fileName })),
                ]}
                value={documentId}
                onChange={setDocumentId}
              />
              <span className="mt-1 block text-xs text-gray-500">
                Faça upload do documento na aba Anexos antes de selecionar aqui.
              </span>
            </div>

            {error && <Alert variant="error">{error}</Alert>}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
              <Button
                type="submit"
                loading={mutation.isPending}
                className="!bg-green-600 hover:!bg-green-700 focus:!ring-green-500"
              >
                {mutation.isPending ? 'Registrando…' : 'Confirmar retorno'}
              </Button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
